//////////////////////////////////////////////////////////////////////////////
// GIS Air Traffic Control Demo: an example of using the GLG Map Server
// integrated in the GLG drawing.
//
// The demo is written in pure HTML5 and JavaScript. The source code of the
// demo uses the GLG Toolkit JavaScript Library supplied by the included
// Glg*.js and GlgToolkit*.js files. The GLG library loads a GLG drawing
// and renders it on a web page, providing an API to handle user interaction
// with graphical objects in the drawing.
//
// The drawings are created using the GLG Graphics Builder, an interactive
// editor that allows to create grahical objects and define their dynamic
// behavior without any programming. The airplane icons are dynamic
// GLG objects created with the Graphics Builder.
//
// The background map is generated by the GLG Map Server, which is integrated
// inside of a GLG drawing as a GLG GIS Object. The GIS Object takes a
// complete care of the map display, automatically generating Map Server
// requests to re-generate the image when the map is panned or zoomed.
//
// The demo demonstrates the use of the asynchronous map loading reques API
// to load a map: when the map is zoomed or  scrolled, the program will continue
// to operate in the old zoom or pan state and will switch to the new zoom or
// pan state when the new map is ready. The GIS Air Traffic Monitoring Demo
// demonstrates an alternative approach that changes the drawing to the new zoom
// or pan state right away, while the new map will appear when it's loaded.
// The USE_REQUEST variable in the code below may be used to change the map
// loading behavior of this demo.
//
// Except for the changes to comply with the JavaScript syntax, this source
// is identical to the source code of the corresponding C/C++/C# and Java
// desktop versions of the demo.
//
// This demo uses the GLG Map Server to display a map.
// The Map Server has to be installed either on the local host or on a
// remote web server. After the Map Server has been installed, modify the
// source code to set the SuppliedMapServerURL variable to point to the
// Map Server location.
//////////////////////////////////////////////////////////////////////////////

/* eslint eqeqeq: 0 */

import { GlgToolkit } from '../GlgToolkitDemo.mod.js'

// Enable general debugging/diagnostics information.
const DEBUG = false;

/* Debugging: set the variable to true to throw an exception on a GLG error
   instead of just displaying an error message on the console.
*/
const DEBUG_GLG_ERRORS = false;

/* Set to true to use asynchronous map requests to load the new map: when the
   map is zoomed or scrolled, the program will continue to operate in the old
   zoom or pan state and will switch to the new zoom or pan when the new map
   is ready.      
   
   If set to false, the map settings will change to the new zoom or pan state 
   right away, and no map will be displayed while the new map is being loaded.
*/
const USE_REQUEST = true;

/* This demo uses the GLG Map Server to display a map.
   The Map Server has to be installed either on the local host or on a
   remote web server. After the Map Server has been installed, set
   SuppliedMapServerURL to point to the Map Server location:

   For example on Linux:
     SuppliedMapServerURL = "http://localhost/cgi-bin/GlmScript";
   or
     SuppliedMapServerURL = "http://www.myserver.com/cgi-bin/GlmScript";

   On Windows:
     SuppliedMapServerURL = "http://localhost/Scripts/GlmScript.pl";
   or
     SuppliedMapServerURL = "http://www.myserver.com/Scripts/GlmScript.pl";

   The SuppliedMapServerURL value overrides the URL of the GIS object in the
   drawing.
*/
const SuppliedMapServerURL = null;    /* String */

const UPDATE_INTERVAL = 50; // Update interval in msec.

// Controls the plane speed used to simulate moving airplanes.
const PLANE_SPEED = 0.002;  // Relative units   

const NUM_NODE_TYPES = 2;   // Number of node icons in the provided palette.
const NUM_PLANE_TYPES = 3;  // Number of airplane icons in the provided palette.

const FLORIDA_ZOOM_DELAY1 = 4000; // Delay to zoom to Florida to show details.
const FLORIDA_ZOOM_DELAY2 = 1000; // Delay to remove Florida zooming message.

/* Set to true to use the orthographic projection instead of switching to the
   rectangular projection for zoomed maps.
*/
const ORTHO_ONLY = false;

/* Scale factors used for changing airplane sizes. */
const
   SMALL_SIZE  = 1.0,
   MEDIUM_SIZE = 1.5,
   BIG_SIZE    = 2.0;

/* Scale factors used for changing node sizes. */
const
   SMALL_MARKER_SIZE  = 7.0,
   MEDIUM_MARKER_SIZE = 9.0,
   BIG_MARKER_SIZE    = 11;

/* Constants used to change airplane colors. */
const
   NORMAL = 0,
   WARNING = 1,
   ALARM = 2,
   SELECTED = 3;

// Global handle to the GLG Toolkit library.
let GLG = new GlgToolkit();

//////////////////////////////////////////////////////////////////////////////
// Creates an instance of the air traffic demo.
// Parameters:
//   glg_div_name  - name of parent div the drawing will be displayed in,
//                   will be passed by the caller.
//   is_mobile     - true if deployed on mobile devices.
//   is_standalone - true if deployed in html, false if deployed in react or
//                   angular.
//   glg_path      - path to the directory where GLG drawings are located.
//////////////////////////////////////////////////////////////////////////////
export function GlgAirTraffic( glg_div_name, glg_path, is_standalone, is_mobile )
{
   Debug( "New script for " + glg_div_name + ": "  +
          Object.getPrototypeOf( this ).constructor.name +
          " glg_path: " + glg_path );
   
   if( DEBUG_GLG_ERRORS )   
     GLG.ThrowExceptionOnError( true, true, true );
   else
     GLG.ThrowExceptionOnError( false, false, false );

   this.GLG_div_name = glg_div_name;
   this.IsMobile = is_mobile;
   this.Standalone = is_standalone;    

   // Use path to the drawings directory is supplied.
   this.GlgPath = glg_path;

   /* Defines an action to perform on touch on mobile devices: 
      select an airplane or scroll the map. On mobile devices, set to false
      to select a plane when it is touched by default. This may be changed
      using the Touch Action buttons.
   */
   this.ScrollMode = ( is_mobile ? false : true );

   /* Array of icons to place on the map as GLG objects in addition to the icons
      defined in GIS server's data. The icons that use GLG objects may be 
      selected with the mouse and their attributes can be changed dynamically, 
      based on data. When the mouse moves over an icon, it may be highlighted 
      with a different color or a tooltip may be displayed. 
   */
   this.NodeArray =
     [ new NodeData( "Boston",        -71.01789,  42.33602 ),
       new NodeData( "New York",      -73.97213,  40.77436 ),
       new NodeData( "San Francisco", -122.55478, 37.79325 ),
       new NodeData( "Miami",         -80.21084,  25.77566 ),
       new NodeData( "Seattle",       -122.35032, 47.62180 ),
       new NodeData( "Houston",       -95.38672,  29.76870 ),
       new NodeData( "Denver",        -104.87265, 39.76803 ),
       new NodeData( "Minneapolis",   -93.26684,  44.96185 ),
       new NodeData( "Chicago",       -87.68496,  41.83705 ),
       new NodeData( "Dallas",        -96.76524,  32.79415 ) ];

   this.NumNodes = this.NodeArray.length;
   this.NumPlanes = 100;              /* Number of simulated planes */
   
   this.NodePool = new Array( NUM_NODE_TYPES );
   this.PlanePool = new Array( NUM_PLANE_TYPES );
   this.NodeTemplate = new Array( NUM_NODE_TYPES );
   this.PlaneTemplate = new Array( NUM_PLANE_TYPES );

   this.Drawing = null;                    /* GlgObject */
   this.PositionObject = null;             /* GlgObject */
   this.PositionArea = null;               /* GlgObject */
   this.TrajectoryTemplate = null;         /* GlgObject */
   this.TrajectoryPool = null;             /* GlgObject */
   this.Map = null;                        /* GlgObject */
   this.FlightInfoPopup = null;            /* GlgObject */
   this.GISObject = null;                  /* GlgObject */
   this.GISArray = null;                   /* GlgObject */
   this.NodeGroup = null;                  /* GlgObject */
   this.PlaneGroup = null;                 /* GlgObject */
   this.TrajectoryGroup = null;            /* GlgObject */
   this.DistancePolygon = null;            /* GlgObject */

   // GlgPoints that store initial extent and center used to reset zoom.
   this.InitExtent = null;                 /* GlgPoint */
   this.InitCenter = null;                 /* GlgPoint */

   this.BaseLayers = null;                 /* String */
   this.NewBaseLayers = null;              /* String */
   this.NewLayers = null;                  /* String */
   this.RequestType = null;                /* String */

   this.PlaneSize = SMALL_SIZE;            /* double */

   this.MapProjection = 0;                 /* int */
   this.NewMapProjection = 0;              /* int */

   this.NewExtent = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.NewCenter = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */

   this.NumTrajectoryPoints = 0;           /* int */
   this.PlaneType = 0;                     /* int */
   this.NodeType = 0;                      /* int */
   this.SelectedPlaneIndex = -1;           /* int */
   this.NumDistancePoints = 0;             /* int */

   this.NewFlags = GLG.GlgGISRequestFlags.GIS_REQUEST_NOTHING;  /* int */
   
   /* Controls asynchronous map requests.
      Don't change: must always be false for initial map loading, will be set
      to the value of USE_REQUEST after that.
      Change USE_REQUEST above to use asynchronous map requests.
   */
   this.UseRequest = false;

   this.MapLoaded = false;                 /* boolean */
   this.InitialLoad = true;                /* boolean */
   this.PerformUpdates = true;             /* boolean */
   this.PanMode = false;                   /* boolean */
   this.DistanceMode = false;              /* boolean */
   this.HasAngle = false;                  /* boolean */
   this.HasElevation = false;              /* boolean */
   this.CityLabels = true;                 /* boolean */
   this.StateDisplay = true;               /* boolean */
   this.DraggingFromButton = false;        /* boolean */
   this.NewCityLabels = false;             /* boolean */
   this.NewCityLabelsFlag = false;         /* boolean */
   
   this.PlaneArray = null;                 /* Array */

   this.gis_request_observer = null;

   // Temporary variables: allocate once.
   this.lat_lon  = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.point    = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.last_xyz = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.curr_xyz = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */

   /* Coefficient for canvas resolution. It will be adjusted in 
      SetCanvasResolution() for mobile devices with HiDPI displays as well as 
      on browser zoom.
   */
   this.CoordScale = 1;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Starts air traffic demo by loading its drawing.
////////////////////////////////////////////////////////////////////////////// 
GlgAirTraffic.prototype.Start = function()
{
   Debug( "Starting: " + this.GLG_div_name );

   this.Active = true;
   
   // Set initial size of the drawing.
   this.SetDrawingSize( false );

   // Show touch action selector for mobile devices.
   if( this.IsMobile )
     ShowElement( this.GLG_div_name + "_touch_action", true );

   /* Load misc. assets such as GLG scrollbars. When assets are loaded, 
      LoadDrawing callback is invoked that loads a GLG drawing defined by
      DrawingName variable.
   */
   this.LoadAssets( ()=>this.LoadDrawing(), null );
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Performs cleanup.
////////////////////////////////////////////////////////////////////////////// 
GlgAirTraffic.prototype.Cleanup = function()
{
   Debug( "Cleanup for: " + this.GLG_div_name );
   
   this.Active = false;    // Ignore any pending updates and callbacks.

   if( this.Drawing )
     this.Drawing.ResetHierarchy();   // Undisplay GLG drawing.

   if( this.ResizeListener )
     window.removeEventListener( "resize", this.ResizeListener );
}

////////////////////////////////////////////////////////////////////////////// 
// Load a GLG drawing from a file.
////////////////////////////////////////////////////////////////////////////// 
GlgAirTraffic.prototype.LoadDrawing = function()
{
   Debug( "LoadDrawing for: " + this.GLG_div_name );
   
   if( !this.Active )
     return;

   /* Load a drawing from the airtraffic.g file. 
      The LoadCB callback will be invoked when the drawing has been loaded.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   let drawing_file = ( this.IsMobile ? "airtraffic2.g" : "airtraffic.g" );   

   GLG.LoadWidgetFromURL( this.GetFullName( drawing_file ), null,
                          this.LoadCB.bind( this ), /*user data*/ null,
                          /*abort test function*/ ()=>!this.Active );
}

////////////////////////////////////////////////////////////////////////////// 
GlgAirTraffic.prototype.GetFullName = function( drawing_name )
{
   if( this.GlgPath == null )
     return drawing_name;

   return this.GlgPath + "/" + drawing_name;
}

//////////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.LoadCB =
  function( /*GlgObject*/ drawing, /*Object*/ user_data, /*String*/ path )
{
   Debug( "LoadCB for: " + this.GLG_div_name );

   if( !this.Active )
     return;

   if( drawing == null )
   {
      AppAlert( "Can't load drawing, check console message for details." );
      return;
   }
   
   if( !document.getElementById( this.GLG_div_name ) )
   {
      Debug( "Can't find " + this.GLG_div_name +
             " div: it may have been removed from the document." );
      return;
   }
    
   // Disable spinning loader.   
   RemoveElement( this.GLG_div_name, "loader_container" );
    
   // Define the element in the HTML page to display the drawing.
   drawing.SetParentElement( this.GLG_div_name );
    
   // Disable viewport border to use the border of the glg_area.
   if( this.Standalone )
     drawing.SetDResource( "LineWidth", 0 );
    
   this.StartAirTrafficDemo( drawing );
}

//////////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.StartAirTrafficDemo = function( drawing )
{
   this.Drawing = drawing;

   this.InitDrawing();
   this.InitIcons();

   this.Drawing.SetupHierarchy();

   this.AddIcons();    // After the hierarchy setup.
   this.Drawing.Update();

   /* After the GIS object has been drawn the first time, we can switch
      to using asynchronous requests for loading GIS images if requested
      by the USE_REQUEST setting.
   */
   this.UseRequest = USE_REQUEST;
   if( this.UseRequest )
   {
      this.gis_request_observer =
        GLG.CreateGlgGISRequestObserver( this.RequestUpdate.bind( this ),
                                         this.RequestAdjustment.bind( this ),
                                         this.AdjustRequest.bind( this ) );

      this.GISObject.SetScrollbarObserver( null, this.gis_request_observer );
   }

   // Start animation.
   setTimeout( ()=>this.UpdatePlanes(), UPDATE_INTERVAL );

   // Zoom to the Florida area after a few seconds to show details.        
   setTimeout( ()=>this.ZoomToFlorida(), FLORIDA_ZOOM_DELAY1 );
}

//////////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.InitDrawing = function()
{
   // Show mouse over tooltips right away.
   this.Drawing.SetDResource( "$config/GlgMouseTooltipTimeout", 0.05 );

   this.Map = this.Drawing.GetResourceObject( "Map" );   
   this.FlightInfoPopup = this.Map.GetResourceObject( "FlightInfoPopup" );
   this.GISObject = this.Map.GetResourceObject( "GISObject" );

   if( SuppliedMapServerURL != null )
     // Override the URL defined in the drawing.
     this.GISObject.SetSResource( "GISMapServerURL", SuppliedMapServerURL );

   // Query and store GIS object's Extent and Center.
   this.InitExtent = this.GISObject.GetGResource( "GISExtent" );
   this.InitCenter = this.GISObject.GetGResource( "GISCenter" );

   // Query and store the GIS projection (ORTHOGRAPHIC or RECTANGULAR)
   this.MapProjection =
     Math.trunc( this.GISObject.GetDResource( "GISProjection" ) );

   /* Set the GIS Zoom mode. It was set and saved with the drawing, 
      but do it again programmatically just in case.
   */
   this.Map.SetZoomMode( null, this.GISObject, null,
                         GLG.GlgZoomMode.GIS_ZOOM_MODE );

   /* Get ID of the GIS Object's GISArray, which holds all icons displayed
      on top of the map in lat/lon coordinates.
   */
   this.GISArray = this.GISObject.GetResourceObject( "GISArray" );

   // Make popup dialog and distance popup invisible.
   this.FlightInfoPopup.SetDResource( "Visibility", 0.0 );
   this.Map.SetDResource( "DistancePopup/Visibility", 0.0 );

   /* Disable the color button: this functionality is not supported with
      the remote map sever setup.
   */
   this.Drawing.SetDResource( "ToggleColor/HandlerDisabled", 1.0 );	  
   
   /* Add Input and Trace callbacks used to handle user interaction. */
   this.Drawing.AddListener( GLG.GlgCallbackType.INPUT_CB,
                             this.InputCallback.bind( this ) );
   this.Drawing.AddListener( GLG.GlgCallbackType.TRACE_CB,
                             this.TraceCallback.bind( this ) );
}

//////////////////////////////////////////////////////////////////////////
// Initializes icons in the drawing
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.InitIcons = function()
{
   let icon_name;

   // Get the palette containing templates for plane and node icons.
   let palette = this.Drawing.GetResourceObject( "Palette" );

   // Delete it from the drawing
   this.Drawing.DeleteThisObject( palette );

   /* Get node and plane templates from the palette. A few sets of templates
      with different level of details are used, depending on the zoom level.
      Palette aproach is used to implement icon types instead of 
      subdrawings, since icons are dynamically added/deleted from the 
      drawing any way to show only the icons visible in the zoomed area.
   */
   for( let i=0; i < NUM_PLANE_TYPES; ++i )
   {
      icon_name = "Plane" + i;
      this.PlaneTemplate[ i ] = palette.GetResourceObject( icon_name );

      // Turn labels on initially. 
      if( this.PlaneTemplate[ i ].GetResourceObject( "Label" ) != null )
        this.PlaneTemplate[ i ].SetDResource( "Label/Visibility", 1.0 );
   }

   this.TrajectoryTemplate = palette.GetResourceObject( "Trajectory" );
   this.NumTrajectoryPoints =
     Math.trunc( this.TrajectoryTemplate.GetDResource( "Factor" ) );

   /* Small filled circles don't look nice in a native renderer.
      Add EDGE to make them look nicer.   
   */
   this.TrajectoryTemplate.SetDResource( "Marker/MarkerType", 
                                         ( GLG.GlgMarkerType.FILLED_CIRCLE |
                                           GLG.GlgMarkerType.CIRCLE ) );
   
   for( let i=0; i < NUM_NODE_TYPES; ++i )
   {
      icon_name = "Node" + i;
      this.NodeTemplate[ i ] = palette.GetResourceObject( icon_name );
   }

   // Create and initialize plane structures used for simulation.
   this.PlaneArray = new Array( this.NumPlanes );
   for( let i=0; i < this.NumPlanes; ++i )
   {
      let plane = new PlaneData();
      plane.name = i.toString();
      plane.color_index = NORMAL;
      this.PlaneArray[ i ] = plane;
      
      this.StartPlane( this.PlaneArray[ i ], true );
   }

   // Create groups to hold nodes and planes.
   this.NodeGroup =
     GLG.CreateObject( GLG.GlgObjectType.ARRAY,
                       GLG.GlgContainerType.GLG_OBJECT, 0, 0, null );
   this.NodeGroup.SetSResource( "Name", "NodeGroup" );

   this.TrajectoryGroup =
     GLG.CreateObject( GLG.GlgObjectType.ARRAY,
                       GLG.GlgContainerType.GLG_OBJECT, 0, 0, null );
   this.TrajectoryGroup.SetSResource( "Name", "TrajectoryGroup" );

   this.PlaneGroup =
     GLG.CreateObject( GLG.GlgObjectType.ARRAY,
                       GLG.GlgContainerType.GLG_OBJECT, 0, 0, null );
   this.PlaneGroup.SetSResource( "Name", "PlaneGroup" );

   /* Add all icons to the GIS object, so that the icon's position may be
      defined in lat/lon. The GIS object handles all details of the 
      GIS coordinate conversion.
   */
   this.GISObject.AddObjectToBottom( this.NodeGroup );
   this.GISObject.AddObjectToBottom( this.PlaneGroup );
   this.GISObject.AddObjectToBottom( this.TrajectoryGroup );

   /* Create groups to keep pooled object icons. Only the icons visible in the
      current view are displayed. Any not used icons are stored in the icon 
      pool.
   */
   for( let i=0; i<NUM_NODE_TYPES; ++i )
     this.NodePool[i] =
       GLG.CreateObject( GLG.GlgObjectType.ARRAY,
                         GLG.GlgContainerType.GLG_OBJECT, 0, 0, null );
   
   for( let i=0; i<NUM_PLANE_TYPES; ++i )
     this.PlanePool[i] =
       GLG.CreateObject( GLG.GlgObjectType.ARRAY,
                         GLG.GlgContainerType.GLG_OBJECT, 0, 0, null );
      
   this.TrajectoryPool =
     GLG.CreateObject( GLG.GlgObjectType.ARRAY,
                       GLG.GlgContainerType.GLG_OBJECT, 0, 0, null );

   this.SetPlaneSize();

   /* The demo starts with the whole word view, then zooms to the Florida area
      in a few seconds to show more details. Set initial parameters
      for the whole world view.
      */
   this.SetupZoomLayers( null );

   this.RequestNewMap( false, false );

   // Store objects used to display lat/lon on mouse move.
   this.PositionArea = this.Drawing.GetResourceObject( "PositionArea" );
   this.PositionObject =
     this.Drawing.GetResourceObject( "PositionLabel/String" );
   this.PositionObject.SetSResource( null, "" );

   // Set Florida zooming message to OFF initially.
   this.Drawing.SetDResource( "Map/FloridaZoomingMessage/Visibility", 0.0 );
}

//////////////////////////////////////////////////////////////////////////
// Invoked after the hierarchy setup.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.AddIcons = function()
{
   // Add visible icons to the drawing and handle zoom level.
   this.UpdateObjectsOnMap( false );

   this.SetMapLoading( true, "Loading map..." );
}

//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SetMapLoading = function( show, message )
{
   if( show )
   {
      this.SetStatus( message );
      this.Map.SetSResource( "LoadingMessage/String", message );
      this.Map.SetDResource( "LoadingMessage/Visibility", 1 );
      this.MapLoaded = false;
   }
   else
   {
      this.SetStatus( "" );
      this.Map.SetDResource( "LoadingMessage/Visibility", 0 );
      this.MapLoaded = true;
   }
}

//////////////////////////////////////////////////////////////////////////
// Makes sure the icons visible at the current zoom level are added
// to the drawing, keeping the rest of the icons in the object pool.
// Also handles the use of more detailed icons depending on the zoom 
// level.
// The object pools are used to handle huge number of icons efficiently.
// For a smaller number of icons, icons may always be kept in the drawing
// and this function is not needed.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.UpdateObjectsOnMap = function( using_request )
{
   // Delete old nodes and planes.
   this.DeleteNodes();
   this.DeletePlanes();
   
   // When using a request, SetupZoomLayers was invoked from AdjustRequest.
   if( !using_request )
   {
      this.SetupZoomLayers( null );
      if( this.NewFlags != 0 )
        this.RequestNewMap( false, false );  // Flush changes into the map.
   }

   this.SetupZoomIcons();

   /* Update the GIS object with new extent but don't draw it yet:
      we want to position icons on the map first.
   */
   this.Map.SetupHierarchy();
   
   for( let i=0; i < this.NumNodes; ++i )      // Position nodes
     this.PositionNode( this.NodeArray[ i ], i );

   /* Add trajectories before positioning planes to setup trajectories' 
      history.
   */
   this.GISObject.AddObjectToBottom( this.TrajectoryGroup );
   
   for( let i=0; i < this.NumPlanes; ++i )     // Position planes
     this.PositionPlane( this.PlaneArray[ i ], i );
   
   // Add new nodes and planes.
   this.GISObject.AddObjectToBottom( this.NodeGroup );
   this.GISObject.AddObjectToBottom( this.PlaneGroup );
   
   this.SelectPlane( this.SelectedPlaneIndex );
}

//////////////////////////////////////////////////////////////////////////
// Set displayed layers depending on zoom level. 
// Also change to rectangular projection for high zoom factors.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SetupZoomLayers = function( request_data )
{
   let base_layers;      /* String */
   let city_labels;      /* boolean */
   
   if( request_data == null )
     this.FlushMapChanges();
   
   let extent = this.GetExtentDegrees( request_data );   /* GlgPoint */

   if( extent.x < 70.0 && extent.y < 70.0 )
   {
      base_layers = "default_air";
      city_labels = true;     // Use city names instead of airport labels.
   }
   else    // Whole world view 
   {
      base_layers = "default_gis,grid70,outline";
      city_labels = false;   // Use airport labels instead of all city names.
   }
   
   // Adjust if changed.
   if( base_layers != this.BaseLayers || city_labels != this.CityLabels )
   {
      let layers = this.CreateLayerString( base_layers, city_labels );
      
      if( request_data != null )
      {
         /* Adjust layers in request data. */
         request_data.layers = layers;
      }
      else
      {
         this.NewLayers = layers;
         this.NewFlags |= GLG.GlgGISRequestFlags.GIS_REQUEST_LAYERS;
      }
      
      this.NewBaseLayers = base_layers;
      this.NewCityLabels = city_labels;
      this.NewCityLabelsFlag = true;
   }

   if( !ORTHO_ONLY )
     this.ChangeProjection( request_data );
}

////////////////////////////////////////////////////////////////////////////////
// Add map layers: airport/city labels and states.
////////////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.CreateLayerString = function( base_layers, city_labels )
{
   let layers = base_layers;
   
   if( city_labels )
     layers += ",us_cities";
   else
     layers += ",-us_cities";
   
   if( this.StateDisplay )   // Add states layer if it is on.
     // Enable states regardless of the default.
     layers += ",states_dcw";
   else
     // Disable state outline display.
     layers += ",-states_dcw";
   
   return layers;
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SetGISLayers = function()
{
   this.NewLayers = this.CreateLayerString( this.BaseLayers, this.CityLabels );
   this.NewFlags = GLG.GlgGISRequestFlags.GIS_REQUEST_LAYERS;
   
   this.RequestNewMap( false, true );
}

//////////////////////////////////////////////////////////////////////////
// Set node types depending on the zoom level.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SetupZoomIcons = function()
{
   let extent = this.GetExtentDegrees( null );   /* GlgPoint */

   if( extent.x < 20.0 && extent.y < 20.0 )
   {
      // High Zoom: use the most detailed icon.
      this.NodeType = 1;
      this.PlaneType = 2;         // Most detailed icon
      this.HasAngle = true;       // Most detailed plane icons show angle
      this.HasElevation = true;   // Most detailed plane icons show elevation
   }
   else if( extent.x < 70.0 && extent.y < 70.0 )
   {
      // Zoom: use detailed icon 1 
      this.NodeType = 1;
      this.PlaneType = 1;         // Detailed icon 
      this.HasAngle = true;       // Detailed plane icons show angle 
      this.HasElevation = false;  
   }
   else
   {
      // Whole world view 
      this.NodeType = 1;          // City icons are always visible 
      this.PlaneType = 0;         // Simple icon 
      this.HasAngle = false;
      this.HasElevation = false;
   }
   
   // Airport labels should be visible only when city labels are off.
   this.NodeTemplate[1].SetDResource( "Label/Visibility",
                                      this.CityLabels ? 0.0 : 1.0 );
}

//////////////////////////////////////////////////////////////////////////
// Change projection to rectangular for zoomed views, and back to 
// orthographics for high-level views.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.ChangeProjection = function( request_data )
{
   let projection = this.GetMapProjection( request_data );

   let extent = this.GetExtentDegrees( request_data );   /* GlgPoint */
   if( extent.x < 30.0 && extent.y < 30.0 )
   {
      if( projection == GLG.GlgProjectionType.RECTANGULAR_PROJECTION )
        return;    // Already rectangular, no change.
      
      // Change to the rectangular projection, extent is already in degrees.
      projection = GLG.GlgProjectionType.RECTANGULAR_PROJECTION;
   }
   else
   {
      if( projection == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
        return;    // Already ortho, no change
      
      // Change to the orthographic projection
      projection = GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION;
      
      // Change extent to meters
      extent.x = extent.x / 90.0 * GLG.EQUATOR_RADIUS;
      extent.y = extent.y / 90.0 * GLG.POLAR_RADIUS;
   }
   
   if( request_data != null )
   {
      // Adjust projection and extent in request data.
      request_data.projection = projection;
      request_data.extent = extent;  // Set extent in degrees
   }
   else
   {
      this.NewMapProjection = projection;
      this.NewExtent = extent;  // Set extent in degrees
      this.NewFlags |= ( GLG.GlgGISRequestFlags.GIS_REQUEST_PROJECTION | 
                         GLG.GlgGISRequestFlags.GIS_REQUEST_EXTENT );
   }
}

//////////////////////////////////////////////////////////////////////////
// Handle user interaction.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.InputCallback = function( viewport, message_obj )
{
   if( !this.Active )
     return;

   let origin = message_obj.GetSResource( "Origin" );
   let format = message_obj.GetSResource( "Format" );
   let action = message_obj.GetSResource( "Action" );
   let subaction = message_obj.GetSResource( "SubAction" );

   if( format == "Button" )         // Handle button clicks
   {
      if( action != "Activate" )
        return;
      
      this.PanMode = false;       // Abort Pan mode

      if( this.DistanceMode )           
      {
         this.AbortDistanceMode();  // Abort the distance mode in progress.

         /* Second click on the Distance button: cancel DistanceMode in
            progress.
         */
         if( origin == "Distance" )
           return;
      }

      this.AbortDistanceMode();
      
      if( origin == "CloseDialog" )
      {
         this.Drawing.SetDResource( "SelectionDialog/Visibility", 0.0 );
         this.Drawing.Update();	 
      }
      else if( origin == "Up" )
      {	    
         this.Zoom( 'u', 0.0 );
      }
      else if( origin == "Down" )
      {	    
         this.Zoom( 'd', 0.0 );
      }
      else if( origin == "Left" )
      {	    
         this.Zoom( 'l', 0.0 );
      }
      else if( origin == "Right" )
      {	    
         this.Zoom( 'r', 0.0 );
      }
      else if( origin == "ZoomIn" )
      {
         this.Zoom( 'i', 2.0 );
      }
      else if( origin == "ZoomOut" )
      {
         this.Zoom( 'o', 2.0 );
      }
      else if( origin == "ZoomReset" )
      {	
         this.Zoom( 'n', 0.0 );
      }
      else if( origin == "ZoomTo" )
      {
         this.Zoom( 't', 0.0 );
      }
      else if( origin == "Pan" )
      {	    
         this.Map.SetZoom( null, 'e', 0.0 );  // Abort ZoomTo/Drag mode
         
         this.PanMode = true;
         this.SetStatus( "Click to define a new center." );
         this.Drawing.Update();	 
      }
      else if( origin == "Drag" )
      {
         /* Activate dragging mode. Dragging will start on the mouse click. 
            If no object of interest is selected by the mouse click, 
            dragging will be started by the code in the Trace callback 
            anyway, but only if no object of interest was selected. 
            The "Drag" button demostrates an alternative way to start 
            dragging from a button which starts dragging even if an object 
            of interest is selected by the mouse click.
         */
         this.DraggingFromButton = true;
         this.Zoom( 's', 0.0 );
         this.SetStatus( "Click and drag the map with the mouse." );
         this.Drawing.Update();
      }
      else if( origin == "AirportLabels" )
      {
         this.CityLabels = !this.CityLabels;
         this.SetGISLayers();
      }
      else if( origin == "ToggleStates" )
      {
         this.StateDisplay = !this.StateDisplay;
         this.SetGISLayers();
      }
      else if( origin == "Planes" )
      {
         ToggleResource( this.Map, "PlaneGroup/Visibility" );
         this.Drawing.Update();	 
      }
      else if( origin == "ValueDisplay" )
      {
         if( this.PlaneType == 0 )
         {
            GLG.Bell();
            this.SetStatus( "Zoom in to see plane labels." );
         }
         else
         {
            // Visibility of all labels is constrained, set just one.
            for( let i=1; i<NUM_PLANE_TYPES; ++i )
              ToggleResource( this.PlaneTemplate[ i ], "Label/Visibility" );
         }
         this.Drawing.Update();	 
      }
      else if( origin == "Update" )
      {
         this.PerformUpdates = !this.PerformUpdates;
         this.Drawing.Update();
      }	
      else if( origin == "PlaneSize" )
      {
         // Change plane icon's size.
         if( this.PlaneSize == SMALL_SIZE )
           this.PlaneSize = MEDIUM_SIZE;
         else if( this.PlaneSize == MEDIUM_SIZE )
           this.PlaneSize = BIG_SIZE;
         else // BIG_SIZE
           this.PlaneSize = SMALL_SIZE;	 
         
         this.SetPlaneSize();
         
         this.Drawing.Update();	 
      }
      else if( origin == "CloseFlightInfo" )
      {
         this.SelectPlane( -1 ); // Unselect the plane and erase popup display.
         this.Drawing.Update();	 
      }	
      else if( origin == "Distance" )
      {
         this.DistanceMode = true;
         this.SetStatus( "Click on the map to define distance to measure," +
                         " press the button again or right click to finish." );
         this.Drawing.Update();
      }
   }
   /* Process mouse clicks on plane icons, implemented as an Action with
      the Plane label attached to an icon and activated on a mouse click. 
   */
   else if( format == "CustomEvent" )
   {	
      /* Ignore selection in the Distance or DraggingFromButton mode,
         as well as in the Scroll mode on mobile devices.
      */
       if( this.DistanceMode || this.DraggingFromButton ||
           ( this.ScrollMode && this.IsMobile ) )
        return;

      let event_label = message_obj.GetSResource( "EventLabel" );
         
      if( event_label == "Plane" )  // Plane icon was selected
      {
         /* Map dragging mode is activated either on a mouse click in the 
            trace callback, or with the Drag toolbar button. 
            Abort the dragging mode if an object with custom event was 
            selected and the dragging was activated on a mouse click. 
            This gives custom events a higher priority compared to the 
            dragging mode. If it's a ZoomTo mode 
            activated by a button, don't abort it and ignore object selection.
         */
         let zoom_mode = this.ZoomToMode();
         if( zoom_mode == 0 ||
             ( zoom_mode & GLG.GlgZoomState.PAN_DRAG_STATE ) != 0 )
         {
            if( zoom_mode != 0 )
              this.Map.SetZoom( null, 'e', 0.0 );  /* Abort zoom mode */
            
            // Get plane index 
            let plane_index =
              Math.trunc( message_obj.GetDResource( "Object/DataIndex" ) );
            this.SelectPlane( plane_index );
               
            // Show message in the bottom
            this.SetStatus( this.PlaneArray[ this.SelectedPlaneIndex ].tooltip );
               
            this.DisplayPlaneInfo();   // Display popup
            this.Drawing.Update();
         }
      }
   }
   else if( action == "Zoom" )
   {
      if( subaction == "End" )
      {
         /* Update icon positions after zooming if not using request.
            If using request, it is done when the request is installed.
         */
         if( !this.UseRequest )
           this.UpdateObjectsOnMap( false );
         
         /* Wait for map loading if the request wasn't aborted by an invalid
            ZoomTo rectangle with zero width / height.
         */
         if( this.GISObject.GetGISRequestInfo( null ) != null )
           this.SetMapLoading( true, "Loading map..." );

         this.Drawing.Update();
      }
      else if( subaction == "Abort" )
        this.SetStatus( "" );
   }
   else if( action == "Pan" )
   {
      if( subaction == "Start" )   // Map dragging start
      {
         this.SetStatus( "Drag the map with the mouse." );
      }
      else if( subaction == "Drag" )    // Dragging
      {
         this.DraggingFromButton = false;
         
         /* Update icon positions after zooming if not using request.
            If using request, it is done when the request is installed.
         */
         if( !this.UseRequest )
         {
            this.UpdateObjectsOnMap( false );
            this.SetMapLoading( true, "Loading map..." );
            this.SetStatus( "Dragging the map with the mouse..." );
         }
         else
           this.SetMapLoading( true, "Release the mouse to show the new map." );

         this.Drawing.Update();
      }
      else if( subaction == "ValueChanged" )   // Scrollbars
      {
         /* Update icon positions after scrolling if not using request.
            If using request, it is done when the request is installed.
         */
         if( !this.UseRequest )
           this.UpdateObjectsOnMap( false );

         this.SetMapLoading( true, "Loading map..." );
         this.Drawing.Update();         
      }
      // Dragging ended or aborted (right mouse button, etc.). 
      else if( subaction == "End" || subaction == "Abort" )
      {
         this.DraggingFromButton = false;
         if( this.UseRequest && subaction != "Abort" )
           this.SetMapLoading( true, "Loading map..." );
         else
           // Reset the message when dragging ends.
           this.SetMapLoading( false, "" );

         this.Map.Update();
      }
   }   
}

//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.ZoomToMode = function()
{
   return Math.trunc( this.Map.GetDResource( "ZoomToMode" ) );
}

//////////////////////////////////////////////////////////////////////////
// Is used to obtain coordinates of the mouse click. 
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.TraceCallback = function( viewport, trace_info )
{      
   if( !this.Active )
     return;

   let event_type = trace_info.event_type;
   let x, y;

   // Use the Map area events only.
   if( !trace_info.viewport.Equals( this.Map ) )
   {
      /* Erase the current postion display when the mouse moves outside 
         of the map.
      */
      switch( event_type )
      {
       case GLG.GlgEventType.MOUSE_MOVED:
         this.PositionObject.SetSResource( null, "" );
         this.PositionArea.Update();
         break;

       default: break;
      }
      return;
   }

   switch( event_type )
   {
    case GLG.GlgEventType.KEY_DOWN:
      if( trace_info.event.keyCode == 27 )      // ESC key
        this.AbortDistanceMode();
      return;

    case GLG.GlgEventType.TOUCH_START:
      if( !this.DraggingFromButton && !this.DistanceMode && !this.ScrollMode )
        return;
      
      GLG.SetTouchMode();        /* Start dragging via touch events. */
      /* Fall through */

    case GLG.GlgEventType.TOUCH_MOVED:
      if( !GLG.GetTouchMode() )
        return;
      /* Fall through */
    case GLG.GlgEventType.MOUSE_PRESSED:
    case GLG.GlgEventType.MOUSE_MOVED:
      x = trace_info.mouse_x * this.CoordScale;
      y = trace_info.mouse_y * this.CoordScale;

      /* COORD_MAPPING_ADJ is added to the cursor coordinates for precise
         pixel mapping.
      */
      x += GLG.COORD_MAPPING_ADJ;
      y += GLG.COORD_MAPPING_ADJ;
      break;

    default: return;
   }
 
   let point_obj;    /* GlgObject */

   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_START:
    case GLG.GlgEventType.MOUSE_PRESSED:
      if( this.ZoomToMode() != 0 )
        return; // ZoomTo or dragging mode in progress: pass it through.

      if( trace_info.button != 1 )
      {
         this.AbortDistanceMode();
         return;  // Use only the left button clicks.
      }
      
      /* Handle paning: set the new map center to the location of the click. */
      if( this.PanMode )
      {
         this.PanMode = false;
         this.SetStatus( "" );
         
         // Converts X/Y to lat/lon using GIS object's current projection.
         this.point.x = x;
         this.point.y = y;
         this.point.z = 0.0;
         this.GISObject.GISConvert( null, GLG.GlgCoordType.SCREEN_COORD,
                                    /* X/Y to Lat/Lon */ true,
                                    this.point, this.lat_lon );
         
         // Pan the map
         this.NewCenter =
           GLG.CreateGlgPoint( this.lat_lon.x, this.lat_lon.y, 0 );
         this.NewFlags = GLG.GlgGISRequestFlags.GIS_REQUEST_CENTER;
         this.RequestNewMap( true, true );
      }
      else if( this.DistanceMode )
      { 
         /* The world coordinates inside the GIS Object's GISArray are in 
            lat/lon.
         */
         this.point.x = x;
         this.point.y = y;
         this.point.z = 0.0;
         this.GISArray.ScreenToWorld( true, this.point, this.lat_lon );
               
         if( this.DistancePolygon == null )
         {
            this.DistancePolygon =
              GLG.CreateObject( GLG.GlgObjectType.POLYGON, 2,
                                null, null, null );
            this.DistancePolygon.SetGResource( "EdgeColor", 1.0, 1.0, 0.0 );
            this.NumDistancePoints = 1;

            point_obj = this.DistancePolygon.GetElement( 0 );
            point_obj.SetGResourceFromPoint( null, this.lat_lon );
               
            point_obj = this.DistancePolygon.GetElement( 1 );
            point_obj.SetGResourceFromPoint( null, this.lat_lon );
               
            this.GISObject.AddObjectToBottom( this.DistancePolygon );
            this.Map.Update();
         }
         else // Not the first point
         {
            // Set current point to the coord. of the click.
            point_obj =
              this.DistancePolygon.GetElement( this.NumDistancePoints );
            point_obj.SetGResourceFromPoint( null, this.lat_lon );
            ++this.NumDistancePoints;
               
            this.DisplayDistance( this.DistancePolygon );
            
            // Add next point, same coords.
            point_obj = point_obj.CopyObject();
            this.DistancePolygon.AddObjectToBottom( point_obj );
               
            this.Map.Update();
         }
      }
      else 
        // Not a Pan or Distance mode: start dragging the map with the mouse.
        this.Zoom( 's', 0.0 );
      break;
      
    case GLG.GlgEventType.TOUCH_MOVED:
    case GLG.GlgEventType.MOUSE_MOVED:
      this.point.x = x;
      this.point.y = y;
      this.point.z = 0;
         
      // The world coordinates inside the GIS Object's GISArray are in lat/lon.
      this.GISArray.ScreenToWorld( true, this.point, this.lat_lon );

      if( this.DistanceMode && this.DistancePolygon != null )
      {
         point_obj = this.DistancePolygon.GetElement( this.NumDistancePoints );
         point_obj.SetGResourceFromPoint( null, this.lat_lon );
         this.DisplayDistance( this.DistancePolygon );
         this.Map.Update();
      }

      // Display lat/lon of a point under the mouse.
      this.PositionObject.SetSResource( null,
                                        CreateLocationString( this.lat_lon ) );
      this.PositionArea.Update();
      break;
         
    default: return;
   }
}

//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.AbortDistanceMode = function()
{
   if( this.DistanceMode )
   {
      if( this.DistancePolygon != null )   // Delete distance polygon
      {
         if( this.GISObject.ContainsObject( this.DistancePolygon ) )
           this.GISObject.DeleteThisObject( this.DistancePolygon );
            
         this.DistancePolygon = null;
      }
      this.Map.SetDResource( "DistancePopup/Visibility", 0.0 );
      this.SetStatus( "" );
      this.DistanceMode = false;
   }
}

//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.DisplayDistance = function( /* GlgObject */ polygon )
{
   // Popup distance display
   this.Map.SetDResource( "DistancePopup/Visibility", 1.0 );
      
   // Last point is for dragging, not set yet - don't include.
   let size = polygon.GetSize();
   if( size < 2 )
     return;
      
   let distance = 0.0;
   let point = null;     /* GlgObject */
   for( let i=0; i<size; ++i )
   {
      let last_point = point;    /* GlgObject */
      point = polygon.GetElement( i );
            
      if( last_point != null )
        // Nautical mile = 1842m 
        distance += GetGlobeDistance( point, last_point ) / 1842.0;
   }
      
   this.Map.SetDResource( "DistancePopup/Distance", distance );
}
   
//////////////////////////////////////////////////////////////////////////
// Returns the length (in meters) of the shortest arc along the earth 
// surface connecting the two points.
//////////////////////////////////////////////////////////////////////////
function GetGlobeDistance( /* GlgObject */ point1_obj,
                           /* GlgObject */ point2_obj )
{
   let lat_lon1, lat_lon2, globe_point1, globe_point2;   /* GlgPoint */
   
   lat_lon1 = point1_obj.GetGResource( null );
   lat_lon2 = point2_obj.GetGResource( null );
      
   // XYZ of the first point, in meters.
   globe_point1 = GetPointXYZ( lat_lon1 );
      
   // XYZ of the second point, in meters.
   globe_point2 = GetPointXYZ( lat_lon2 );
      
   let dx = globe_point1.x - globe_point2.x;
   let dy = globe_point1.y - globe_point2.y;
   let dz = globe_point1.z - globe_point2.z;

   // Length of a straight line between the points.
   let straight_dist = Math.sqrt( dx * dx + dy * dy + dz * dz );
   
   // Use the average value.
   let globe_radius = ( GLG.EQUATOR_RADIUS + GLG.POLAR_RADIUS ) / 2.0;

   // The length of the shortest connecting arc along the earth surface. 
   let arc_dist = 2.0 * globe_radius *
     Math.asin( straight_dist / ( 2.0 * globe_radius ) );
   
   return arc_dist;
}

//////////////////////////////////////////////////////////////////////////
function GetPointXYZ( /* GlgPoint */ lat_lon )     /* GlgPoint */
{
   let
     angle_x,
     angle_y;
   
   let xyz =  GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */

   /* For simplicity, place the origin at the intersection of the x axis 
      with the globe surface. */
   
   angle_x = DegToRad( lat_lon.x );
   angle_y = DegToRad( lat_lon.y );
      
   xyz.x = GLG.EQUATOR_RADIUS * Math.cos( angle_x ) * Math.cos( angle_y );
   xyz.y = GLG.EQUATOR_RADIUS * Math.sin( angle_y );
   xyz.z = GLG.EQUATOR_RADIUS * Math.sin( angle_x ) * Math.cos( angle_y );

   return xyz;
}

//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.Zoom = function( type, value )
{
   switch( type )
   {
    default:
      if( this.UseRequest )
      {
         /* Prepare new map image in a separate thread. The requested image
            will be installed using InstallGISRequest when it is ready.
            CheckScrollLimits will be invoked from AdjustRequest, and
            UpdateObjectsOnMap will be invoked when the request is installed.
         */

         this.Map.RequestGISZoom( null, type, value,
                                  this.gis_request_observer );
         this.RequestType = type;
      }
      else
      {
         this.Map.SetZoom( null, type, value );
         this.RequestType = type;
         
         this.CheckScrollLimits( null );
         this.UpdateObjectsOnMap( false );
      }

      this.SetMapLoading( true, "Loading map..." );
      this.Drawing.Update();
      break;

    case 'n':
      // Reset map to the initial extent.
      this.NewMapProjection = GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION;
      this.NewCenter = GLG.CopyGlgPoint( this.InitCenter );
      this.NewExtent = GLG.CopyGlgPoint( this.InitExtent );
      this.NewFlags = ( GLG.GlgGISRequestFlags.GIS_REQUEST_PROJECTION | 
                        GLG.GlgGISRequestFlags.GIS_REQUEST_CENTER | 
                        GLG.GlgGISRequestFlags.GIS_REQUEST_EXTENT );
      
      this.RequestNewMap( true, true );
      break;
      
    case 't':
      // Start ZoomTo
      if( this.UseRequest )
        this.Map.RequestGISZoom( null, 't', 0.0, this.gis_request_observer );
      else
        this.Map.SetZoom( null, 't', 0.0 );
      
      this.SetStatus( "Define a rectangular area to zoom to." );
      this.Drawing.Update();	 
      break;
      
    case 's':
      // Start dragging the map with the mouse
      if( this.UseRequest )
        this.Map.RequestGISZoom( null, 's', 0.0, this.gis_request_observer );
      else
        this.Map.SetZoom( null, 's', 0.0 );
      
      this.Drawing.Update();	 
      break;
   }
}

//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.RequestNewMap =
  function( /* boolean */ update_objects, /* boolean */ call_update )
{
   if( this.NewFlags == 0 )
     return;   // No change
   
   if( this.UseRequest )
   {
      /* Prepare new map image in a separate thread. The requested image
         will be installed using InstallGISRequest when it is ready.
         StoreNewValues and UpdateObjectsOnMap will be invoked when the 
         request is installed.
      */
      this.GISObject.RequestGISMap( null, this.NewExtent.x, this.NewExtent.y, 
                                    this.NewCenter.x, this.NewCenter.y, 0,
                                    this.NewMapProjection, this.NewLayers,
                                    this.NewFlags, this.gis_request_observer );
      this.NewFlags = 0;
   }
   else   /* Set GIS object parameters directly and let it load the map. */
   {
      if( ( this.NewFlags & GLG.GlgGISRequestFlags.GIS_REQUEST_PROJECTION ) != 0 )
        this.GISObject.SetDResource( "GISProjection", this.NewMapProjection );
      if( ( this.NewFlags & GLG.GlgGISRequestFlags.GIS_REQUEST_EXTENT ) != 0 )
        this.GISObject.SetGResourceFromPoint( "GISExtent", this.NewExtent );
      if( ( this.NewFlags & GLG.GlgGISRequestFlags.GIS_REQUEST_CENTER ) != 0 )
        this.GISObject.SetGResourceFromPoint( "GISCenter", this.NewCenter );
      if( ( this.NewFlags & GLG.GlgGISRequestFlags.GIS_REQUEST_LAYERS ) != 0 )
        this.GISObject.SetSResource( "GISLayers", this.NewLayers );
      this.NewFlags = 0;
      
      this.StoreNewValues();

      if( update_objects )
        this.UpdateObjectsOnMap( false );
      if( call_update )
        this.Drawing.Update();
   }

   this.SetMapLoading( true, "Loading map..." );
}
  
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.StoreNewValues = function()
{
   if( this.NewBaseLayers != null )
   {
      this.BaseLayers = this.NewBaseLayers;
      this.NewBaseLayers = null;
   }
   
   if( this.NewCityLabelsFlag )
   {
      this.CityLabels = this.NewCityLabels;
      this.NewCityLabelsFlag = false;
   }
   
   // Store a possibly changed projection.
   this.MapProjection =
     Math.trunc( this.GISObject.GetDResource( "GISProjection" ) );
}

//////////////////////////////////////////////////////////////////////////
// For rectangular projection on the detailed map, make sure the map 
// does not scroll beyond the poles in the vertical direction.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.CheckScrollLimits = function( request_data )
{
   let extent, center;       /* GlgPoint */
   let min_y, max_y;         /* double */
   let adjust_x, adjust_y;   /* boolean */
      
   /* Since CheckScrollLimits may be invoked with null request data in 
      non-request mode (USE_REQUEST=false), we can't use request_data.flags
      to determine request type. Instead, the RequestType variable 
      is used to indicate request type for both modes.
   */
   let type = this.RequestType;
   this.RequestType = null;

   switch( type )
   {
    case 'u':  // Scroll up
    case 'd':  // Scroll down
    case 'l':  // Scroll left
    case 'r':  // Scroll right
      break; // Adjust only for scroll types.
    default: return;   // Don't adjust for other zoom types.
   }
      
   let projection = this.GetMapProjection( request_data );
   if( projection == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
     return;   // Allow unlimited scrolling for ortho.
      
   extent = this.GetMapExtent( request_data );
   center = this.GetMapCenter( request_data );

   min_y = center.y - extent.y / 2.0;
   max_y = center.y + extent.y / 2.0;
   
   /* Check and adjust center lat to make sure the map does not scroll 
      beyond the poles in the vertical direction. 
   */
   adjust_y = true;
   if( extent.y >= 180.0 )
     center.y = 0.0;
   else if( min_y < -90.0 )
     center.y = -90.0 + extent.y / 2.0;
   else if( max_y > 90.0 )
     center.y = 90.0 - extent.y / 2.0;
   else
     adjust_y = false;
   
   /* Allow scrolling to +-180 in horizontal direction, to match the
      range of the horizontal scrollbar.
   */
   adjust_x = true;
   if( center.x < -180.0 )
     center.x = -180.0;
   else if( center.x > 180.0 )
     center.x = 180.0;
   else
     adjust_x = false;
   
   /* Set adjusted center. If called from AdjustRequest, it's already
      modified in-place in request_data - do nothing.
   */
   if( request_data == null && ( adjust_x != 0 || adjust_y != 0 ) )
     this.GISObject.SetGResourceFromPoint( "GISCenter", center );
}

////////////////////////////////////////////////////////////////////////
// Changes plane color to indicate selection and displayes or erases the 
// flight info popup dialog.
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SelectPlane = function( selected_plane_index )
{
   let plane;              /* PlaneData */
   let popup_visibility;   /* double */
      
   if( this.SelectedPlaneIndex != -1 )   // Unselect previously selected plane
   {
      plane = this.PlaneArray[ this.SelectedPlaneIndex ];
      if( plane.graphics != null )    // Restore color if plane is visible 
        plane.graphics.SetDResource( "ColorIndex", plane.color_index );
   }
      
   if( selected_plane_index != -1 )   // Select new plane
   {
      plane = this.PlaneArray[ selected_plane_index ];
      if( plane.graphics != null ) // Set selected color if plane is visible
        plane.graphics.SetDResource( "ColorIndex", SELECTED );
   }
   
   // Display or erase the flight info popup.
   if( selected_plane_index == -1 )    // Unselected
     popup_visibility = 0.0;
   else   // Selected
     popup_visibility = 1.0;
   this.FlightInfoPopup.SetDResource( "Visibility", popup_visibility );
   
   this.SelectedPlaneIndex = selected_plane_index;
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.DisplayPlaneInfo = function()
{
   let plane = this.PlaneArray[ this.SelectedPlaneIndex ];    /* PlaneData */
   this.FlightInfoPopup.SetSResource( "FlightInfo", plane.tooltip );
   this.FlightInfoPopup.SetDResource( "Elevation", GetPlaneElevation( plane ) );
   this.FlightInfoPopup.SetDResource( "StatusIndex", plane.color_index );
   
   this.FlightInfoPopup.SetSResource( "Location",
                                      CreateLocationString( plane.lat_lon ) );
}
   
////////////////////////////////////////////////////////////////////////
// Delete node icons and place them into the object pool. 
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.DeleteNodes = function()
{
   // Move node icons into the object pool. 
   let size = this.NodeGroup.GetSize();
   for( let i=0; i<size; ++i )
   {
      let icon = this.NodeGroup.GetElement( i );     /* GlgObject */
      this.NodePool[ this.NodeType ].AddObjectToBottom( icon );
   }

   // Delete node icons from the drawing and node group 
   this.GISObject.DeleteThisObject( this.NodeGroup );
   for( let i=0; i<size; ++i )
     this.NodeGroup.DeleteBottomObject();
      
   // Set nodes' graphics to null 
   if( size > 0 )
     for( let i=0; i<this.NumNodes; ++i )
       this.NodeArray[i].graphics = null;
}

////////////////////////////////////////////////////////////////////////
// Delete plane icons and place them into the object pool. 
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.DeletePlanes = function()
{
   // Move plane icons into the object pool.
   let size = this.PlaneGroup.GetSize();
   for( let i=0; i<size; ++i )
   {
      let icon = this.PlaneGroup.GetElement( i );   /* GlgObject */
      this.PlanePool[ this.PlaneType ].AddObjectToBottom( icon );
   }
   
   // Delete plane icons from the drawing and plane group 
   this.GISObject.DeleteThisObject( this.PlaneGroup );
   for( let i=0; i<size; ++i )
     this.PlaneGroup.DeleteBottomObject();
   
   // Set planes' graphics to null 
   if( size > 0 )
     for( let i=0; i<this.NumPlanes; ++i )
       this.PlaneArray[i].graphics = null;
   
   this.DeleteTrajectories();
}

////////////////////////////////////////////////////////////////////////
// Delete trajectory objects and place them into the object pool. 
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.DeleteTrajectories = function()
{
   // Move plane icons into the object pool.
   let size = this.TrajectoryGroup.GetSize();
   for( let i=0; i<size; ++i )
   {	
      let icon = this.TrajectoryGroup.GetElement( i );   /* GlgObject */
      this.TrajectoryPool.AddObjectToBottom( icon );
   }
      
   // Delete trajectory icons from the drawing and trajectory group
   this.GISObject.DeleteThisObject( this.TrajectoryGroup );
   for( let i=0; i<size; ++i )
     this.TrajectoryGroup.DeleteBottomObject();
   
   // Set trajectorys' graphics to null
   if( size > 0 )
     for( let i=0; i<this.NumPlanes; ++i )
       this.PlaneArray[i].trajectory = null;
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.PositionNode = function( /* NodeData */ node, index )
{
   if( !this.IconVisible( node.lat_lon ) )
     return;
   
   // Add node's graphics to the drawing if first time.
   if( node.graphics == null )
     this.AddNodeGraphics( node, this.NodeType, index );
   
   /* Position node's icon. Since the icons are added as children of the 
      GIS Object, their coordinates are specified in lat/lon. The GIS Object
      handles all details of coordinate convesion.
   */
   node.graphics.SetGResourceFromPoint( "Position", node.lat_lon );
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.CreateNodeIcon = function( /* int */ node_type )
{
   let icon;      /* GlgObject */
      
   let size = this.NodePool[ node_type ].GetSize();
   if( size > 0 )   // Return an icon from the pool
   {
      icon = this.NodePool[ node_type ].GetElement( size - 1 );
      this.NodePool[ node_type ].DeleteBottomObject();
   }
   else   // Create a new icon
   {
      icon =
        this.NodeTemplate[ node_type ].CloneObject( GLG.GlgCloneType.STRONG_CLONE );
   }
   return icon;
}
   
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.CreatePlaneIcon = function( /* int */ plane_type )
{
   let icon;   /* GlgObject */

   let size = this.PlanePool[ plane_type ].GetSize();
   if( size > 0 )   // Return an icon from the pool
   {
      icon = this.PlanePool[ plane_type ].GetElement( size - 1 );
      this.PlanePool[ plane_type ].DeleteBottomObject();
   }
   else   // Create a new icon 
   {
      icon = 
        this.PlaneTemplate[ plane_type ].CloneObject( GLG.GlgCloneType.STRONG_CLONE );
   }
   return icon;
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.CreateTrajectoryIcon = function()   /* GlgObject */
{
   let icon;   /* GlgObject */
   
   let size = this.TrajectoryPool.GetSize();
   if( size > 0 )   // Return an icon from the pool
   {
      icon = this.TrajectoryPool.GetElement( size - 1 );
      this.TrajectoryPool.DeleteBottomObject();
   }
   else   // Create a new icon 
   {
      icon =
        this.TrajectoryTemplate.CloneObject( GLG.GlgCloneType.STRONG_CLONE );
   }
   return icon;
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.PositionPlane = function( /* PlaneData */ plane, index )
{
   // Gets the new plane's position, simulated or from real data.
   GetPlaneLatLon( plane );
   
   if( !this.IconVisible( plane.lat_lon ) )
   {
      // Delete graphics and place into the pool
      if( plane.graphics != null )
      {
         this.PlanePool[ this.PlaneType ].AddObjectToBottom( plane.graphics );
         this.PlaneGroup.DeleteThisObject( plane.graphics );
         plane.graphics = null;
      }

      // Delete trajectory and place into the pool
      if( plane.trajectory != null )
      {
         this.TrajectoryPool.AddObjectToBottom( plane.trajectory );
         this.TrajectoryGroup.DeleteThisObject( plane.trajectory );
         plane.trajectory = null;
      }
      return;
   }
   
   // Add the plane icon to the drawing if the first time.
   if( plane.graphics == null  )
     this.AddPlaneGraphics( plane, this.PlaneType, index );

   /* Position plane's icon. Since the icons are added as children of the 
      GIS Object, their coordinates are specified in lat/lon. The GIS Object
      handles all details of coordinate convesion.
   */
   plane.graphics.SetGResourceFromPoint( "Position", plane.lat_lon );
      
   // Update icon's direction angle if necessary.
   if( this.HasAngle )
     plane.graphics.SetDResource( "Angle", this.GetPlaneAngle( plane ) );

   if( this.HasElevation )
     plane.graphics.SetDResource( "Height", GetPlaneElevation( plane ) );
   
   if( plane.trajectory != null )
   {
      /* For small speeds, skip a few iterations to increase the 
         trajectory's length.
      */
      if( PLANE_SPEED < 0.01 )  
      {
         let n = Math.trunc( 0.01 / PLANE_SPEED );
         if( n != 0 )
         {
            ++plane.iteration;
            if( ( plane.iteration % n ) != 0 )
              return;   // Skip n iterations, update every n'th
         }
      }      
      
      plane.trajectory.SetDResource( "VisEntryPoint", 1.0 );
      plane.trajectory.SetGResourceFromPoint( "XYEntryPoint", plane.lat_lon );
   }
}

////////////////////////////////////////////////////////////////////////
// Adds an airport icon, fills labels, tooltips, etc.
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.AddNodeGraphics =
  function( /* NodeData */ node, /* int */ node_type, index )
{      
   let icon = this.CreateNodeIcon( node_type );    /* GlgObject */
      
   // Index for direct access 
   icon.SetDResource( "DataIndex", index );
      
   if( node_type > 0 )   // More detailed icon
     icon.SetSResource( "LabelString", node.name );
      
   let tooltip = node.name + ", " + CreateLocationString( node.lat_lon );
   icon.SetSResource( "TooltipString", tooltip );
      
   node.graphics = icon;
      
   // The node will be positioned after the GIS object is setup.
      
   this.NodeGroup.AddObjectToBottom( icon );
}

////////////////////////////////////////////////////////////////////////
// Adds a plane icon, fills labels, tooltips, etc.
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.AddPlaneGraphics = function( /* PlaneData */ plane, /* int */ plane_type, index )
{
   let icon = this.CreatePlaneIcon( plane_type );   /* GlgObject */
      
   // Index for direct access
   icon.SetDResource( "DataIndex", index );
      
   // Icon color
   icon.SetDResource( "ColorIndex", plane.color_index );
      
   if( plane_type > 0 )   // More detailed icon
   {
      // Show the flight number as icon label
      let label = "Flight " + plane.flight_number;
      icon.SetSResource( "LabelString", label );
   }
      
   // Set the tooltip
   icon.SetSResource( "TooltipString", plane.tooltip );
      
   plane.graphics = icon;
   
   this.PlaneGroup.AddObjectToBottom( icon );
   
   if( plane_type == 2 )   // For detailed icon, create trajectory
   {
      icon = this.CreateTrajectoryIcon();
      plane.trajectory = icon;
      
      // Set entries invisible initially
      icon.SetDResource( "Marker/Visibility", 0.0 );
         
      this.TrajectoryGroup.AddObjectToBottom( icon );
         
      for( let i=0; i<this.NumTrajectoryPoints; ++i )  // Set fading
        icon.SetDResource( "BrightEntryPoint",
                           0.2 + 0.8 * i / this.NumTrajectoryPoints );
   }
}

////////////////////////////////////////////////////////////////////////
// Check if the icon is visible in the current zoom region.
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.IconVisible = function( /* GlgPoint */ lat_lon_pos )
{
   // Converts lat/lon to X/Y using GIS object's current projection.
   this.GISObject.GISConvert( null, GLG.GlgCoordType.OBJECT_COORD,
                              /* Lat/Lon to XY */ false, lat_lon_pos,
                              this.point );

   return ( this.point.z >= 0.0 &&
            this.point.x > -1100.0 && this.point.x < 1100.0 &&
            this.point.y > -1100.0 && this.point.y < 1100.0 );
}

////////////////////////////////////////////////////////////////////////
// Displays a message in the status area.
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SetStatus = function( message )
{
   this.Drawing.SetSResource( "StatusLabel/String", message );
   this.Drawing.GetResourceObject( "StatusArea" ).Update();
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SetPlaneSize = function()
{
   for( let i=0; i<NUM_PLANE_TYPES; ++i )
   {
      /* GlgObject */
      let resource = this.PlaneTemplate[ i ].GetResourceObject( "IconScale" );
      if( resource != null )
      {
         // Polygon icon: set scale.
         resource.SetDResource( null, this.PlaneSize );
      }
      else
      {
         resource =
           this.PlaneTemplate[ i ].GetResourceObject( "Marker/MarkerSize" );
         if( resource != null )
         {
            // Marker: set MarkerSize.
            if( this.PlaneSize == SMALL_SIZE )
              resource.SetDResource( null, SMALL_MARKER_SIZE );
            else if( this.PlaneSize == MEDIUM_SIZE )
              resource.SetDResource( null, MEDIUM_MARKER_SIZE );
            else
              resource.SetDResource( null, BIG_MARKER_SIZE );
         }
      }
   }
}

////////////////////////////////////////////////////////////////////////
// Toggle resource between 0 and 1.
////////////////////////////////////////////////////////////////////////
function ToggleResource( /* GlgObject */ obj, res_name )
{
   let resource = obj.GetResourceObject( res_name );   /* GlgObject  */
   if( resource == null )
     return;
   
   let value = resource.GetDResource( null );
   resource.SetDResource( null, value != 0.0 ? 0.0 : 1.0 );
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.GetMapProjection =
  function( /* GlgGISRequestData */ request_data )
{
   if( request_data != null )
     return request_data.projection;
   else
   {
      this.FlushMapChanges();
      return this.MapProjection;
   }
}
 
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.GetMapExtent =
  function( /* GlgGISRequestData */ request_data )   /* GlgPoint */
{   
   if( request_data != null )
     return request_data.extent;
   else
   {
      this.FlushMapChanges();
      return this.GISObject.GetGResource( "GISExtent" );
   }
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.GetMapCenter =
  function( /* GlgGISRequestData */ request_data )   /* GlgPoint */
{
   if( request_data != null )
     return request_data.center;
   else
   {
      this.FlushMapChanges();
      return this.GISObject.GetGResource( "GISCenter" );
   }
}

////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.FlushMapChanges = function()
{
   // Flush new data into the map before querying if the map has changed.
   if( this.NewFlags != 0 )
     this.RequestNewMap( false, false );
}

////////////////////////////////////////////////////////////////////////
// Gets extent in lat/lon (converts from meters for ortho projection).
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.GetExtentDegrees =
  function( /* GlgGISRequestData */ request_data )  /* GlgPoint */
{   
   let extent = this.GetMapExtent( request_data );   /* GlgPoint */
   
   let projection = this.GetMapProjection( request_data );
   if( projection == GLG.GlgProjectionType.ORTHOGRAPHIC_PROJECTION )
   {
      if( request_data != null )
        // Create a copy: will be modified.
        extent = GLG.CopyGlgPoint( extent );
      
      // Convert meters to degrees,
      extent.x = extent.x / GLG.EQUATOR_RADIUS * 90.0;
      extent.y = extent.y / GLG.POLAR_RADIUS * 90.0;
   }
   return extent;
}

//////////////////////////////////////////////////////////////////////////
function RELATIVE_TO_NEW_RANGE( low, high, rel_value )
{
   return ( low + ( high - low ) * rel_value );
}

//////////////////////////////////////////////////////////////////////////
function DegToRad( angle )
{
   return angle / 180.0 * Math.PI;
}

//////////////////////////////////////////////////////////////////////////
function RadToDeg( angle )
{
   return angle / Math.PI * 180.0;
}

//////////////////////////////////////////////////////////////////////////
// UTILITY FUNCTION: Calculates an angle between the line defined by two 
// points and the X axis.
//////////////////////////////////////////////////////////////////////////
function GetAngle( /* GlgPoint */ pt1, /* GlgPoint */ pt2 )
{
   let length, angle;
      
   length = GetLength( pt1, pt2 );
      
   if( length == 0.0  )
     angle = 0.0;
   else
   {
      angle = Math.acos( ( pt2.x - pt1.x ) / length );
      
      if( pt2.y - pt1.y < 0.0 )  // ScreenSpace Z axis points to the user.
        angle = - angle;
   }
        
   return RadToDeg( angle );
}

//////////////////////////////////////////////////////////////////////////
// UTILITY FUNCTION: Calculates a distance between two points in 2D.
//////////////////////////////////////////////////////////////////////////
function GetLength( /* GlgPoint */ pt1, /* GlgPoint */ pt2 )
{
   return Math.sqrt( ( pt2.x - pt1.x ) * ( pt2.x - pt1.x ) +
                     ( pt2.y - pt1.y ) * ( pt2.y - pt1.y ) );
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Converts plane lat/lon to the GLG world 
//     coordinates for calculating plane speed and directional angle.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.GetPlanePosition =
  function( /* PlaneData */ plane, /* GlgPoint */ xyz )
{
   // Converts lat/lon to X/Y using GIS object's current projection.
   this.GISObject.GISConvert( null, GLG.GlgCoordType.OBJECT_COORD, 
                              /* Lat/Lon to XY */ false, plane.lat_lon, xyz );
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Converts node lat/lon to the GLG world 
//     coordinates for calculating plane's initial directional angle.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.GetNodePosition =
  function( /* NodeData */ node, /* GlgPoint */ xyz )
{
   // Converts lat/lon to X/Y using GIS object's current projection.
   this.GISObject.GISConvert( null, GLG.GlgCoordType.OBJECT_COORD, 
                              /* Lat/Lon to XY */ false, node.lat_lon, xyz );
}

//////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Calculates plane icon's directional angle.
//     In an application, it will query the plane's directional angle.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.GetPlaneAngle = function( /* PlaneData */ plane )
{
   /* Rectangular projection preserves straight lines, we can use the 
      angle of the line connecting the start and end nodes. For the
      orthographic projection, use this case if the plane has just started
      and there is no previous position stored.
   */
   if( this.MapProjection == GLG.GlgProjectionType.RECTANGULAR_PROJECTION ||
       plane.path_position == plane.path_position_last )   // Just started
   {
      this.GetNodePosition( plane.from_node, this.last_xyz );
      this.GetNodePosition( plane.to_node, this.curr_xyz );
   }
   else  /* In the orthographic projection straight lines are drawn as 
            curves. Use the angle of the line connecting the current and 
            last position of the plane. */
   {
      let stored_position;
      
      stored_position = plane.path_position;    // Store current position.
      
      // Get coordinates of the plane's previous position
      plane.path_position = plane.path_position_last;
      GetPlaneLatLon( plane );
      this.GetPlanePosition( plane, this.last_xyz );
      
      // Restore the plane's current position and get its coordinates.
      plane.path_position = stored_position;
      GetPlaneLatLon( plane );
      this.GetPlanePosition( plane, this.curr_xyz );
   }
   
   /* Calculate the angle of a line connecting the previous and 
      current position.
   */
   return GetAngle( this.last_xyz, this.curr_xyz );
}

////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Calculates plane icon's elevation.
//     In an application, it will query the plane elevation.
//
// For the simulation, it calculated the elevation using zero at the start
// and end of the path and the maximum elevation in the middle.
////////////////////////////////////////////////////////////////////////
function GetPlaneElevation( /* PlaneData */ plane )
{
   return ( 0.5 - Math.abs( plane.path_position - 0.5 ) ) * 2.0 * 10000.0;   
}

////////////////////////////////////////////////////////////////////////
// Updates moving icons with the new position data.
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.UpdatePlanes = function()
{
   if( !this.Active )
     return;
   
   /* If UseRequest=true, the map loading message will be erased when the 
      request is installed.
   */
   if( ( ( this.InitialLoad || !this.UseRequest ) && !this.MapLoaded ) &&
       Math.trunc( this.GISObject.GetDResource( "ImageLoaded" ) ) != 0 )
   {
      this.SetMapLoading( false, "" );
      this.InitialLoad = false;
   }
   
   if( this.PerformUpdates )
   {     
      for( let i = 0; i < this.NumPlanes; ++i )
        this.UpdatePlane( this.PlaneArray[ i ], i );
      
      if( this.SelectedPlaneIndex != -1 )  // Update selected plane info if any 
        this.DisplayPlaneInfo();
      
      this.Drawing.Update();
   }
   
   // Restart update timer.
   setTimeout( ()=>this.UpdatePlanes(), UPDATE_INTERVAL );
}

////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Calculates new plane position using 
// simulated data. In an application, it will query the plane's lat/lon.
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.UpdatePlane = function( /* PlaneData */ plane, index )
{      
   if( plane.from_node == null || plane.to_node == null )
     return;    // Plane is not in the air - no start/destination node.
   
   // Finished the old path, start a new one.
   if( plane.path_position == 1.0 )
   {
      if( index == this.SelectedPlaneIndex )
        this.SelectPlane( -1 );   // Unselect the plane: it reached destination
      
      this.StartPlane( plane, false );
   }
   else  // Continue on the current path.
   {
      // Store last position for calculating angle in ORTHO projection. 
      plane.path_position_last = plane.path_position;
      
      plane.path_position += plane.speed * PLANE_SPEED;
      if( plane.path_position > 1.0 )
        plane.path_position = 1.0;
   }

   SetPlaneColor( plane );
   
   this.PositionPlane( plane, index );   // Position plane on the map
}

////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Simulate data to change plane color to show 
//     warnings and alarms. In an application, it will query the plane's 
//     status.
////////////////////////////////////////////////////////////////////////
function SetPlaneColor( /* PlaneData */ plane )
{
   let new_color_index = NORMAL;

   // Set random color
   let random_value = GLG.Rand( 0.0, 1.0 );
   if( plane.color_index == NORMAL )
   {
      if( random_value <= 0.999 )
        new_color_index = NORMAL;
      else if( random_value > 0.9999 )
        new_color_index = ALARM;
      else if( random_value > 0.999 )
        new_color_index = WARNING;     
   }
   else if( plane.color_index == WARNING )
   {
      if( random_value > 0.99 )
        new_color_index = NORMAL;
      else
        new_color_index = plane.color_index;   // Keep alarm for a while
   }
   else if( plane.color_index == ALARM )
   {
      if( random_value > 0.999 )
        new_color_index = NORMAL;
      else
        new_color_index = plane.color_index;   // Keep alarm for a while
   }
   
   if( plane.graphics != null && 
       new_color_index != plane.color_index )
     plane.graphics.SetDResource( "ColorIndex", new_color_index );
   
   plane.color_index = new_color_index;
}

////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Starts simulation for a plane, selects its 
// start and end nodes. 
////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.StartPlane =
  function( /* PlaneData */ plane, /* boolean */ init )
{
   let
     to_index,
     from_index;
      
   if( this.NumNodes < 2 )
     AppAlert( "Less then two nodes: can't start planes." );

   from_index = Math.trunc( GLG.Rand( 0.0, this.NumNodes - 0.001 ) );
   do
   {
      to_index = Math.trunc( GLG.Rand( 0.0, this.NumNodes - 0.001 ) );
   } while( to_index == from_index );

   plane.from_node = this.NodeArray[ from_index ];
   plane.to_node = this.NodeArray[ to_index ];
   plane.flight_number = Math.trunc( GLG.Rand( 101.0, 1999.0 ) );
   plane.speed = GLG.Rand( 0.4, 1.0 );   // Vary plane speed 
      
   if( init )   // Init the demo: position randomly along the path 
   {
      plane.path_position = GLG.Rand( 0.1, 0.9 );
      plane.path_position_last = plane.path_position - 0.05;  // For angle
   }
   else         // Position at the beginning of the path 
   {
      plane.path_position = 0.0;
      plane.path_position_last = 0.0;
   }
   
   let flight_name = "Flight " + plane.flight_number;
      
   // Add from/to node info to the tooltip.
   plane.tooltip = flight_name + " from "  +
     plane.from_node.name + " to " +  plane.to_node.name;

   // Set all trajectory points invisible, if any 
   if( plane.trajectory != null )
     plane.trajectory.SetDResource( "Marker%/Visibility", 0.0 );
}

////////////////////////////////////////////////////////////////////////
// FOR FLIGHT SIMULATION ONLY: Calculates plane's lat/lon using simulated 
//     data. In an application, it will query the plane's position.
//
// The simulation moves the plane from the start to the end node/city
// as controlled by the path_position parameter. The path_position changes
// in the range from from 0 (start node) to 1 (end node).
////////////////////////////////////////////////////////////////////////
function GetPlaneLatLon( /* PlaneData */ plane )
{
   plane.lat_lon.x = 
     RELATIVE_TO_NEW_RANGE( plane.from_node.lat_lon.x, 
                            plane.to_node.lat_lon.x, plane.path_position );
   plane.lat_lon.y = 
     RELATIVE_TO_NEW_RANGE( plane.from_node.lat_lon.y, 
                            plane.to_node.lat_lon.y, plane.path_position );
}

//////////////////////////////////////////////////////////////////////////
// Generate a location info string by converting +- sign info into the
// N/S, E/W suffixes, and decimal fraction to deg, min, sec.
//////////////////////////////////////////////////////////////////////////
function CreateLocationString( /* GlgPoint */ point )   /* String */
{
   let x_deg, y_deg, x_min, y_min, x_sec, y_sec;  /* int */
   let char_x, char_y;  /* String */
   let lat, lon;        /* double */

   if( point.z < 0.0 )
     return "";

   lon = point.x;
   lat = point.y;
   
   if( lon < 0.0 )
   {
      lon = -lon;
      char_x = 'W';
   }
   else if( lon >= 360.0 )
   {
      lon -= 360.0;
      char_x = 'E';
   }
   else if( lon >= 180.0 )
   {
      lon = 180.0 - ( lon - 180.0 );
      char_x = 'W';
   }
   else
     char_x = 'E';
   
   if( lat < 0.0 )
   {
      lat = -lat;
      char_y = 'S';
   }
   else
     char_y = 'N';
   
   x_deg = Math.trunc( lon );
   x_min = Math.trunc( ( lon - x_deg ) * 60.0 );
   x_sec = Math.trunc( ( lon - x_deg - x_min / 60.0 ) * 3600.0 );
   
   y_deg = Math.trunc( lat );
   y_min = Math.trunc( ( lat - y_deg ) * 60.0 );
   y_sec = Math.trunc( ( lat - y_deg - y_min / 60.0 ) * 3600.0 );
   
   let location_string =
     "Lon=" + x_deg + "\u00B0" + 
          GLG.PrintfI( "%02d", x_min ) + "'" + 
          GLG.PrintfI( "%02d", x_sec ) + '"' + char_x +
     "  Lat=" + y_deg + "\u00B0" + 
          GLG.PrintfI( "%02d", y_min ) + "'" + 
          GLG.PrintfI( "%02d", y_sec ) + '"' + char_y;
   return location_string;
}

//////////////////////////////////////////////////////////////////////////
// Zoom to the Florida area after a few seconds to show details.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.ZoomToFlorida = function()
{      
   if( !this.Active )
     return;
   
   this.AbortDistanceMode();

   // Show zoom message.
   this.Drawing.SetDResource( "Map/FloridaZoomingMessage/Visibility", 1.0 );
   this.Drawing.Update();

   /* GISExtent is in lon/lat for the rectangular GIS projection,
      and in meters for the orthographic projection.
      To find proper values, zoom the map in the GlgBuilder and 
      copy the GISExtent values. */
   
   // Zoom to the Florida boundaries on detailed map.
   this.NewExtent = GLG.CreateGlgPoint( 1169530.0, 1169530.0, 0.0 );
   this.NewCenter = GLG.CreateGlgPoint( -82.8239, 28.9382, 0.0 );
   this.NewFlags = ( GLG.GlgGISRequestFlags.GIS_REQUEST_EXTENT |
                     GLG.GlgGISRequestFlags.GIS_REQUEST_CENTER );

   this.RequestNewMap( true, false );

   /* Reorder Florida zoom message to the top, in case any objects 
      were added on top of it.
   */
   let florida_message =
     this.Drawing.GetResourceObject( "Map/FloridaZoomingMessage" );
   this.Map.ReorderElement( this.Map.GetIndex( florida_message ),
                            this.Map.GetSize() - 1 );
   this.Drawing.Update();

   // Erase zoom message after a delay.
   setTimeout( ()=>this.ZoomToFloridaEnd(), FLORIDA_ZOOM_DELAY2 );
}

//////////////////////////////////////////////////////////////////////////
// Remove the Florida zooming message after a few seconds.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.ZoomToFloridaEnd = function()
{
   if( !this.Active )
     return;
   
   this.Drawing.SetDResource( "Map/FloridaZoomingMessage/Visibility", 0.0 );
   this.Drawing.Update();
}

//////////////////////////////////////////////////////////////////////////
// GIS request observer interface: requests adjustement callbacks.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.RequestAdjustment = function()
{
   if( !this.Active )
     return false;

   return true;
}

//////////////////////////////////////////////////////////////////////////
// GIS request observer interface: adjustement callback.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.AdjustRequest =
  function( /* GlgGISRequestData */ request_data )
{
   if( !this.Active )
     return false;

   let flags = request_data.flags;

   if( ( flags & GLG.GlgGISRequestFlags.GIS_REQUEST_EXTENT ) != 0 )
   {
      /* Change layers only if zoom changed, to allow user to toggle
         CityLabels.
      */
      this.SetupZoomLayers( request_data );
      
      if( ( flags & GLG.GlgGISRequestFlags.GIS_REQUEST_CENTER ) != 0 ||
          ( flags & GLG.GlgGISRequestFlags.GIS_REQUEST_PROJECTION ) != 0 )
        this.CheckScrollLimits( request_data );
   }
   return true;
}

//////////////////////////////////////////////////////////////////////////
// GIS request observer interface: receives status update notifications.
//////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.RequestUpdate =
  function( /* GlgObject */ gis_object, status )
{
   if( !this.Active )
     return;

   switch( status )
   {
    case GLG.GlgGISRequestStatus.GIS_REQUEST_READY:
      if( gis_object.InstallGISRequest( null ) )
      {
         this.StoreNewValues();
         
         this.UpdateObjectsOnMap( true );
         this.Drawing.Update();
      }
      else
        this.SetStatus( "Failed to install map request." );
      break;
      
    default:
      /* A mouse click in the drawing invokes the Trace callback which 
         activates pan zoom mode to pan the map by dragging it with the mouse.
         If the click selected the airplane, the zoom mode is aborted to
         process airplane selection instead, which generates this diagnostic 
         message.
      */         
      AppLog( "Aborting previous GIS request." ); 
      break;
   }

   this.SetMapLoading( false, "" );
}

//////////////////////////////////////////////////////////////////////////////
// Loads any assets required by the application and invokes the specified
// callback when done.
// Alternatively, the application drawing can be loaded as an asset here
// as well, so that it starts loading without waiting for the other assets 
// to finish loading.
//////////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.LoadAssets = function( callback, user_data )
{
   Debug( "LoadAssets for: " + this.GLG_div_name );
   
   if( !this.Active )
     return;

   /* Define an internal variable to keep the number of loaded assets. */
   this.NumLoadedAssets = 0;

   /* HTML5 doesn't provide a scrollbar input element (only a range input 
      html element is available). This application needs to load GLG scrollbars
      used for integrated chart scrolling. For each loaded scrollbar, the 
      AssetLoaded callback is invoked with the supplied data array parameter.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_h.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_h", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_v.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_v", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );   
}

//////////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.AssetLoaded = function( glg_object, data, path )
{
   if( !this.Active )
     return;
   
   if( data.name == "scrollbar_h" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgHScrollbar", glg_object );
   }
   else if( data.name == "scrollbar_v" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgVScrollbar", glg_object );
   }
   else
     AppError( "Unexpected asset name" );

   ++this.NumLoadedAssets;
    
   // Invoke the callback after all assets have been loaded.
   if( this.NumLoadedAssets == 2 )
     data.callback( data.user_data );
}

//////////////////////////////////////////////////////////////////////////////
// Changes drawing size while maintaining width/height aspect ratio.
//////////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SetDrawingSize = function( next_size )
{
   const ASPECT_RATIO = 750 / 650;
   
   const MIN_WIDTH = 600;
   const MAX_WIDTH = 1000;
   const SCROLLBAR_WIDTH = 15;

   let span = document.body.clientWidth - SCROLLBAR_WIDTH;    

   if( this.SizeIndex == undefined )   // First time: initialize.
   {
      this.SizeIndex = 0;

      if( this.IsMobile )
      {
         /* Mobile devices use fixed device-width: disable Change Drawing Size 
            button. If it's not standalone, it is handled outside of this 
            script.
         */
         if( this.Standalone )
           RemoveElement( null, this.GLG_div_name + "_change_size" );
      }
      else   /* Desktop */
      {      
         const small_sizes  = [ 1, 1.5,  2.,   2.5 ];
         const medium_sizes = [ 1, 0.75, 1.25, 1.5 ];
         const large_sizes  = [ 1, 0.6,  1.25, 1.5 ];
         
         if( span < 600 )
           this.SetDrawingSize.size_array = small_sizes;
         else if( span < 800 )
           this.SetDrawingSize.size_array = medium_sizes;
         else
           this.SetDrawingSize.size_array = large_sizes;
         
         this.SetDrawingSize.num_sizes = this.SetDrawingSize.size_array.length;
         
         /* Handle browser zooming. */
         this.ResizeListener = ()=>this.SetDrawingSize( false );
         window.addEventListener( "resize", this.ResizeListener );
      }
   }
   else if( next_size )
   {
      ++this.SizeIndex;
      this.SizeIndex %= this.SetDrawingSize.num_sizes;
   }
    
   let drawing_area = document.getElementById( this.GLG_div_name );
   if( this.IsMobile )
   {
      /* Mobile devices use constant device-width, adjust only the height 
         of the drawing to keep the aspect ratio.
      */
      drawing_area.style.height =
        "" + Math.trunc( drawing_area.clientWidth / ASPECT_RATIO ) + "px";
   }
   else   /* Desktop */
   {
      let start_width;
      if( span < MIN_WIDTH )
        start_width = MIN_WIDTH;
      else if( span > MAX_WIDTH )
        start_width = MAX_WIDTH;
      else
        start_width = span;

      let size_coeff = this.SetDrawingSize.size_array[ this.SizeIndex ];
      let width = Math.trunc( Math.max( start_width * size_coeff, MIN_WIDTH ) );
      drawing_area.style.width = "" + width + "px";
      drawing_area.style.height = 
        "" + Math.trunc( width / ASPECT_RATIO ) + "px";
   }

   // Adjust canvas resolution for mobile devices and browser zoom state.
   if( !next_size )
     this.SetCanvasResolution();
}

//////////////////////////////////////////////////////////////////////////////
// Increases canvas resolution for mobile devices with HiDPI displays and for
// browser zooming. Sets CoordScale global variable.
//////////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SetCanvasResolution = function()
{
   let TextScale, PixelOffsetScale, ScreenCoordScale, NativeWidgetTextScale;

   if( this.IsMobile )
   {
      /* CoordScale parameter defines canvas coordinate scaling.
         Values greater than 1 increase canvas resolution and result in 
         sharper rendering. On mobile devices with devicePixelRatio > 1,
         the value of devicePixelRatio may be used for very crisp rendering
         with very thin lines. For pixel ration greater than 2, limit 
         CoordScale to 2 to draw thicker lines.

         CanvasScale > 1 makes text smaller. The TextScale parameter defines
         the text scaling factor used to increase text size.

         The ScreenCoordScale parameter specifies a scaling factor for fixed
         scale viewports that use screen coordinates. If the size of the fixed
         scale viewport (such as a height of a toolbar) is controlled by a pixel
         offset transformation, ScreenCoordScale may be set to the same value as
         PixelOffsetScale to scale the content of the viewport proportionally
         to its height increase.

         The NativeWidgetTextScale parameter defines the scaling factor that is
         used to scale down text in native widgets (such as native buttons, 
         toggles, etc.) to match the scale of the drawing.
      */
      if( window.devicePixelRatio > 2. )
        this.CoordScale = 2.;
      else
        this.CoordScale = window.devicePixelRatio;
      
      if( this.IsMobile )
        TextScale = 1.75;
      else
        TextScale = 1.3;

      PixelOffsetScale = TextScale;
      ScreenCoordScale = PixelOffsetScale;
      NativeWidgetTextScale = 0.6;
   }
   else   // Desktop
   {
      /* Change canvas resolution to match browser zoom state. */
      this.CoordScale = window.devicePixelRatio;      
      TextScale = window.devicePixelRatio;
      PixelOffsetScale = window.devicePixelRatio;
      ScreenCoordScale = window.devicePixelRatio;
      NativeWidgetTextScale = 1.;    // Don't change native text size.
   }

   GLG.SetCanvasScale( this.CoordScale, TextScale, NativeWidgetTextScale,
                       PixelOffsetScale, ScreenCoordScale );
}

//////////////////////////////////////////////////////////////////////////
function RemoveElement( parent_id, child_id )
{
   let element;
   if( parent_id == null )
     // No parent was provided: remove from document.
     element = document.getElementById( child_id );
   else
   {
      // Parent ID was provided: remove from parent.
      let parent = document.getElementById( parent_id );
      if( parent == null )
        return;
      element = parent.querySelector( "#" + child_id );
   }
   
   if( element != null )
     element.parentNode.removeChild( element );
}

//////////////////////////////////////////////////////////////////////////
export function ShowElement( name, state )
{
   let element = document.getElementById( name );
   if( element != null )
     element.style.display = ( state ? "inline-block" : "none" );
}

//////////////////////////////////////////////////////////////////////////////
// Changes action to be performed on touch move on mobile devices.
//////////////////////////////////////////////////////////////////////////////
GlgAirTraffic.prototype.SetTouchAction = function()
{
   let scroll_mode_elem =
     document.getElementById( this.GLG_div_name + "_scroll_mode" );
   this.ScrollMode = scroll_mode_elem.checked;
}

//////////////////////////////////////////////////////////////////////////////
function Debug( message )
{
   if( DEBUG )
     console.log( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppAlert( message )
{
   window.alert( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppError( message )
{
   console.error( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppLog( message )
{
   console.log( message );
}

//////////////////////////////////////////////////////////////////////////////
function NodeData( name, lon, lat )
{
   this.name = name;      /* String */
   this.lat_lon = GLG.CreateGlgPoint( lon, lat, 0 );   /* GlgPoint */
   this.graphics = null;  /* GlgObject */
}
   
//////////////////////////////////////////////////////////////////////////////
function PlaneData()
{
   this.name = null;            /* String */
   this.lat_lon = GLG.CreateGlgPoint( 0, 0, 0 ); /* GlgPoint */
   this.flight_number = 0;      /* int */
   this.tooltip = null;         /* String */
   this.graphics = null;        /* GlgObject */
   this.trajectory = null;      /* GlgObject */
   this.from_node = null;       /* NodeData */
   this.to_node = null;         /* NodeData */
   this.path_position = 0;      /* double */
   this.path_position_last = 0; /* double */
   this.speed = 0;              /* double */
   this.color_index = 0;        /* int */
   this.iteration = 0;          /* int */
}
